import {
  Button,
  Form,
  Input,
  Select,
  Space,
  Typography,
  Switch,
  notification,
  Divider,
  Skeleton,
  DatePicker,
} from 'antd';
import moment from 'moment';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useLazyGetBranchesForSystemQuery } from '@app/api/Branches';
import {
  useLazyGetPositionsForSystemQuery,
  useUpdatePositionMutation,
} from '@app/api/Positions';
import { useUpdateUserMutation } from '@app/api/User/api';
import {
  Error403Unauthorized,
  FastCreationModal,
  Phone,
} from '@app/components';
import { CustomAlert } from '@app/components/CustomAlert';
import type { PhoneValidation } from '@app/components/Phone/types';
import { phoneValidateHandler } from '@app/components/Phone/utils';
import { SetBranchesComponent } from '@app/components/SetBranchesComponent';
import {
  ADD_POSITION_LABEL,
  BRANCHES_LABEL,
  DATE_PICKER_FORMAT,
  OFF,
  ONE_TIME_CODE,
  ERROR,
  ERROR_REQUIRED_FIELD,
  SUCCESS,
  WARNING,
  ADD_MORE_USER,
  ADD_USER,
  rangePickerLocale,
  GENDER,
  PREFIX,
  ISO_FORMAT_INDEX,
  EMAIL_TYPE_VALIDATION,
  EMAIL_LENGTH_VALIDATION,
  PASSWORD_VALIDATION_RULES,
} from '@app/constants';
import {
  useMemoizeObject,
  useUserPrint,
  useUsersAccessRights,
} from '@app/hooks';
import { LeavePageContext, NotificationContext } from '@app/providers';
import type { NNotificationProvider } from '@app/providers/NotificationProvider/NotificationProvider.types';
import { handleCatchError } from '@app/services';
import { selectCountries, selectUser, useAppSelector } from '@app/store';
import type {
  NBranches,
  NDictionaries,
  NEmployees,
  NSmallGlobalEntities,
  NUsers,
  NUtils,
} from '@app/types';
import { afterUserUpdate } from '@app/utils';

import type { ValuesType } from '@pages/Users/AddUser/types';

import '@pages/Users/AddUser/styles/AddUser.desktop.scss';
import '@pages/Users/AddUser/styles/AddUser.tablet.scss';

const { Text } = Typography;
const { TextArea } = Input;

export const AddUser = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const { userId, database, activeBranchId, activeBranchCountryId } =
    useUserPrint();
  const [isSaveButtonLoader, setSaveButtonLoader] = useState(false);
  const [isAddMoreButtonLoader, setAddMoreButtonLoader] = useState(false);
  const [error, setError] = useState('');
  const [positions, setPositions] = useState<NSmallGlobalEntities.ModelItem[]>(
    []
  );
  const [open, setOpen] = useState(false);
  const [selectedBranches, setSelectedBranches] = useState<
    NBranches.ModelBranchItem[]
  >([]);
  const [allBranches, setAllBranches] = useState<NBranches.ModelBranchItem[]>(
    []
  );
  const [isAdmin, setIsAdmin] = useState(false);
  const [submitType, setSubmitType] = useState('');
  const [isGlobalLoader, setGlobalLoader] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [errorType, setErrorType] = useState<NUtils.ErrorTypes>(undefined);

  const { showNotification } = useContext(NotificationContext);

  const { telCode } = useAppSelector(selectUser);

  const [modalForm] = Form.useForm();

  const [, contextHolder] = notification.useNotification();

  const [phoneValidation, setPhoneValidation] = useState<PhoneValidation>({
    isValid: false,
    errorMessage: null,
  });

  const countries = useAppSelector(selectCountries);

  const {
    hasUserRightToCreate: hasUserRightToCreateUser,
    hasUserRightToCreatePosition,
    hasUserRightToChangeOwner,
    hasUserRightToChangeBranches,
  } = useUsersAccessRights();

  const {
    setNewData,
    prepareStartedData,
    setInitialData,
    newData,
    setInputData,
    setSelectorData,
    setDatePickerData,
    setSwitchData,
  } = useContext(LeavePageContext);

  const memoizedAllBranches = useMemoizeObject(allBranches);
  const memoizedSelectedBranches = useMemoizeObject(selectedBranches);
  const memoizedCountries = useMemoizeObject(countries);

  const [getBranchesForSystem] = useLazyGetBranchesForSystemQuery();
  const [getPositionsForSystem] = useLazyGetPositionsForSystemQuery();
  const [updatePosition] = useUpdatePositionMutation();
  const [updateUser] = useUpdateUserMutation();

  const showUserModal = () => {
    setOpen(true);
  };

  const hideUserModal = () => {
    setOpen(false);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const countryObj = (memoizedCountries || []).find(
          (item: NDictionaries.ModelCountry) => item.TelCode === telCode
        );
        prepareStartedData({
          lastName: undefined,
          firstName: undefined,
          middleName: undefined,
          email: undefined,
          password: undefined,
          phone: '',
          position: undefined,
          gender: undefined,
          isAdmin: false,
          dateBirth: null,
          description: undefined,
          alterIsEmployee: false,
          telegramId: undefined,
          UserBranchIds: [activeBranchId],
          phoneCountryId: countryObj?.Id,
        });

        setGlobalLoader(true);

        if (activeBranchId) {
          const positionsResponse = await getPositionsForSystem({
            ActiveBranchId: activeBranchId,
          }).unwrap();

          setPositions([
            { Id: 0, IdS: null, Name: 'Без должности' },
            ...positionsResponse,
          ]);

          const branchesResponse = await getBranchesForSystem({
            ActiveBranchId: activeBranchId,
          }).unwrap();
          setAllBranches(branchesResponse);
        }
        setGlobalLoader(false);
      } catch (error) {
        showNotification(
          (error as Error).message,
          (error as Error).cause as NNotificationProvider.NotifyType
        );
      }
    };

    fetchData();
  }, [
    activeBranchId,
    database,
    userId,
    telCode,
    memoizedCountries,
    showNotification,
    prepareStartedData,
    getBranchesForSystem,
    getPositionsForSystem,
  ]);

  useEffect(() => {
    const currentBranch = (memoizedAllBranches || []).find(
      ({ Id: localId }) => localId === activeBranchId
    );
    if (
      !(memoizedSelectedBranches || []).find(
        ({ Id: localId }) => localId === activeBranchId
      )
    ) {
      currentBranch &&
        setSelectedBranches((prev) => prev.concat(currentBranch));
    }
  }, [activeBranchId, memoizedAllBranches, memoizedSelectedBranches]);

  const sendDataToDb = useCallback(
    async (values: ValuesType) => {
      const {
        lastName,
        firstName,
        middleName,
        email,
        password,
        position,
        gender,
        isAdmin,
        dateBirth,
        description,
        alterIsEmployee,
        telegramId,
        Phone: { Phone, CountryId },
        note,
      } = values;

      const { errorMessage, isValid } = phoneValidation;

      const mainPhoneError =
        errorMessage || (!isValid ? 'Обязательное поле' : '');

      if (mainPhoneError) {
        form.setFields([
          {
            name: 'Phone',
            errors: [mainPhoneError],
          },
        ]);
        return;
      }

      const data = {} as NUsers.ModelUser;
      const handledGender =
        gender === '' || gender === undefined
          ? null
          : (gender as NUsers.ModelUser['Gender']);

      data.NameFirst = firstName;
      data.NameMiddle = middleName;
      data.NameLast = lastName;
      data.Email = email;
      data.Password = password || null;
      data.Phone = Phone;
      data.PhoneCountryId = CountryId;
      data.PositionId = position === 0 ? null : position;
      data.Gender = handledGender;
      data.DateBirth = dateBirth
        ? dateBirth.format(DATE_PICKER_FORMAT[ISO_FORMAT_INDEX])
        : null;
      data.IsAdmin = isAdmin;
      data.UserBranchIds = selectedBranches.length
        ? selectedBranches.filter((item) => item !== null).map(({ Id }) => Id)
        : null;
      data.Description = description;
      data.Note = note;
      data.TelegramId = telegramId || null;
      data.IsEmployee = false;
      data.IsSaveAdditions = false;
      data.AlterIsEmployee = Boolean(alterIsEmployee);
      data.PhoneCode = null;
      data.IsDeleted = false;
      data.Photo = null;
      data.DateWorkStart = null;
      data.DatePatentEnd = null;
      data.PassportInfo = null;
      data.TaxPayerId = null;
      data.InsuranceCertificateN = null;
      data.Citizenship = null;
      data.IsTerminated = false;
      data.MigrateId = null;
      data.IsFakeEmail = false;
      data.IsFakePhone = false;

      if (submitType === ADD_USER) {
        setSaveButtonLoader(true);
      } else {
        setAddMoreButtonLoader(true);
      }

      const saveUser = async () => {
        try {
          if (userId && database && activeBranchId) {
            const { Id: createdUserId } = await updateUser({
              data,
              ActiveBranchId: activeBranchId,
              logs: { isLogged: true, userId, database },
            }).unwrap();

            prepareStartedData({});

            afterUserUpdate({
              id: data.Id,
              alterIsEmployee: data.AlterIsEmployee || false,
              createdUserId,
              navigate,
              submitType,
              form,
              isEditPage: false,
            });
          }
        } catch (error) {
          setError(handleCatchError(error));
          setErrorType(ERROR);
        } finally {
          setSaveButtonLoader(false);
          setAddMoreButtonLoader(false);
        }
      };

      saveUser();
    },
    [
      form,
      selectedBranches,
      submitType,
      userId,
      database,
      activeBranchId,
      setError,
      navigate,
      prepareStartedData,
      phoneValidation,
      updateUser,
    ]
  );

  const addUser = async () => {
    setSubmitType(ADD_USER);
    form.submit();
  };

  const addMoreUser = async () => {
    setSubmitType(ADD_MORE_USER);
    form.submit();
    setInitialData({
      newData,
    });
  };

  const addPosition = () => {
    showModal();
  };

  const showModal = () => {
    setIsModalOpen(true);
  };

  const handleOk = () => {
    modalForm.submit();
    setIsModalOpen(false);
  };

  const handleCancel = () => {
    modalForm.resetFields();
    setIsModalOpen(false);
  };

  const finishModal: NEmployees.FinishModal = async ({ Name, Description }) => {
    try {
      await updatePosition({
        ActiveBranchId: activeBranchId,
        data: {
          Id: 0,
          Name: Name,
          Description: Description,
          IsDeleted: false,
          MigrateId: null,
        },
      }).unwrap();

      showNotification('Должность успешно добавлена', SUCCESS);

      const response = await getPositionsForSystem({
        ActiveBranchId: activeBranchId,
      }).unwrap();

      const positionsWithEmpty = [
        { Id: 0, IdS: null, Name: 'Без должности' },
        ...response,
      ];

      setPositions(positionsWithEmpty);

      const selectedPosition = positionsWithEmpty.find(
        ({ Name: localName }) => localName === Name
      );

      form.setFieldValue('position', selectedPosition?.Id);
    } catch (error) {
      if ((error as Error).cause === ERROR) {
        showNotification(
          (error as Error).message,
          (error as Error).cause as NNotificationProvider.NotifyType
        );
      } else if ((error as Error).cause === WARNING) {
        setError((error as Error).message);
      }
    }
  };

  if (!hasUserRightToCreateUser) {
    return <Error403Unauthorized />;
  }

  if (isGlobalLoader) {
    return <Skeleton active />;
  }

  return (
    <>
      {contextHolder}
      <Form
        form={form}
        className={`${PREFIX}add-user bc-form`}
        name="basic"
        style={{ maxWidth: '800px', width: '100%' }}
        initialValues={{
          remember: true,
          Phone: { Phone: '', CountryId: activeBranchCountryId || 1 },
        }}
        autoComplete={OFF}
        onFinish={sendDataToDb}
        scrollToFirstError={true}
        onFinishFailed={() => {
          phoneValidateHandler(form, phoneValidation);
        }}
      >
        <Space
          direction="vertical"
          size="middle"
          className="form-space"
          style={{ width: '100%' }}
        >
          <div className="container">
            <Text strong className="label label-require" data-testid="name">
              Имя
            </Text>
            <Form.Item
              name="firstName"
              rules={[{ required: true, message: ERROR_REQUIRED_FIELD }]}
              className="mg-0 second-column"
            >
              <Input
                autoComplete={ONE_TIME_CODE}
                size="large"
                maxLength={60}
                className="second-column"
                data-testid="name-input"
                onChange={(e) => {
                  setInputData({
                    event: e,
                    fieldName: 'firstName',
                    isEditPage: false,
                    isTextField: true,
                  });
                }}
              />
            </Form.Item>
          </div>
          <div className="container">
            <Text strong className="label" data-testid="surname">
              Фамилия
            </Text>
            <Form.Item name="lastName" className="mg-0 second-column">
              <Input
                autoComplete={ONE_TIME_CODE}
                size="large"
                maxLength={60}
                className="second-column"
                data-testid="surname-input"
                onChange={(e) => {
                  setInputData({
                    event: e,
                    fieldName: 'lastName',
                    isEditPage: false,
                    isTextField: true,
                  });
                }}
              />
            </Form.Item>
          </div>
          <div className="container">
            <Text strong className="label" data-testid="patronymic">
              Отчество
            </Text>
            <Form.Item name="middleName" className="mg-0 second-column">
              <Input
                autoComplete={ONE_TIME_CODE}
                size="large"
                maxLength={80}
                data-testid="patronymic-input"
                onChange={(e) => {
                  setInputData({
                    event: e,
                    fieldName: 'middleName',
                    isEditPage: false,
                    isTextField: true,
                  });
                }}
              />
            </Form.Item>
          </div>
          <Form.Item className="mg-0">
            <div className="container">
              <Text
                strong
                className="label"
                data-testid="full-name-in-parental-case-title"
              >
                ФИО в родительном падеже
              </Text>
            </div>
          </Form.Item>
          <Form.Item className="mg-0">
            <div className="container">
              <Text
                strong
                className="label"
                data-testid="full-name-in-dative-case-title"
              >
                ФИО в дательном падеже
              </Text>
            </div>
          </Form.Item>
          <div className="container">
            <div className="email-label">
              <Text strong className="label label-require" data-testid="email">
                Email
              </Text>
              <br />
              <Text type="secondary">
                На почту будет отправлено приглашение
              </Text>
            </div>
            <Form.Item
              name="email"
              rules={[
                { required: true, message: ERROR_REQUIRED_FIELD },
                EMAIL_TYPE_VALIDATION,
                EMAIL_LENGTH_VALIDATION,
              ]}
              className="mg-0 second-column"
            >
              <Input
                autoComplete={ONE_TIME_CODE}
                size="large"
                data-testid="email-input"
                onChange={(e) => {
                  setInputData({
                    event: e,
                    fieldName: 'email',
                    isEditPage: false,
                    isTextField: true,
                  });
                }}
                maxLength={100}
              />
            </Form.Item>
          </div>
          <div className="container">
            <Text strong className="label" data-testid="new-password">
              Пароль
            </Text>

            <Form.Item
              name="password"
              rules={[...PASSWORD_VALIDATION_RULES]}
              className="mg-0 second-column"
            >
              <Input.Password
                data-testid="new-password-input"
                autoComplete={ONE_TIME_CODE}
                minLength={6}
                maxLength={60}
                size="large"
                onChange={(e) => {
                  setInputData({
                    event: e,
                    fieldName: 'password',
                    isEditPage: false,
                    isTextField: true,
                  });
                }}
              />
            </Form.Item>
          </div>
          <div className="container">
            <Text strong className="label label-require" data-testid="phone">
              Мобильный телефон
            </Text>

            <Form.Item name="Phone" className="mg-0 second-column">
              <Phone
                name="Phone"
                required
                onValidate={setPhoneValidation}
                phoneInputDataTestId="phone-input"
              />
            </Form.Item>
          </div>
          <div className="container">
            <Text strong className="label" data-testid="telegram">
              Telegram
            </Text>
            <Form.Item name="telegramId" className="mg-0 second-column">
              <Input
                autoComplete={ONE_TIME_CODE}
                size="large"
                maxLength={100}
                className="second-column"
                data-testid="telegram-input"
                onChange={(e) => {
                  setInputData({
                    event: e,
                    fieldName: 'telegramId',
                    isEditPage: false,
                    isTextField: true,
                  });
                }}
              />
            </Form.Item>
          </div>
          <div className="container">
            <Typography.Text className="label" strong data-testid="position">
              Должность
            </Typography.Text>

            <Form.Item name="position" className="mg-0 second-column">
              <Select
                size="large"
                showSearch
                optionFilterProp="label"
                data-testid="position-select"
                options={positions.map((position) => {
                  return {
                    label: position.Name,
                    value: position.Id,
                  };
                })}
                popupRender={(menu) => (
                  <>
                    {hasUserRightToCreatePosition ? (
                      <>
                        <Button type="link" size="small" onClick={addPosition}>
                          {ADD_POSITION_LABEL}
                        </Button>
                        <Divider style={{ margin: '8px 0' }} />
                        {menu}
                      </>
                    ) : (
                      <>{menu}</>
                    )}
                  </>
                )}
                onChange={(value) => {
                  setSelectorData({ value, fieldName: 'position' });
                }}
              />
            </Form.Item>
          </div>
          <div className="container">
            <Text strong className="label" data-testid="gender">
              Пол
            </Text>
            <Form.Item name="gender" className="mg-0 second-column">
              <Select
                className="field-selector"
                size="large"
                options={GENDER}
                data-testid="gender-select"
                onChange={(value) => {
                  form.setFieldsValue({ gender: value });
                  setSelectorData({ value, fieldName: 'gender' });
                }}
              />
            </Form.Item>
          </div>
          <div className="container">
            <Text strong className="label" data-testid="birth-date">
              Дата рождения
            </Text>
            <Form.Item
              name="dateBirth"
              className="mg-0 date-picker second-column"
            >
              <DatePicker
                autoComplete={ONE_TIME_CODE}
                locale={rangePickerLocale}
                size="large"
                format={DATE_PICKER_FORMAT}
                data-testid="birth-date-select"
                onChange={(value) => {
                  setDatePickerData({
                    dateString: value,
                    fieldName: 'dateBirth',
                  });
                }}
                disabledDate={(current) =>
                  current.unix() >= moment().startOf('day').unix()
                }
              />
            </Form.Item>
          </div>
          <div className="container" style={{ alignItems: 'flex-start' }}>
            <Text strong className="label" data-testid="description">
              Описание
            </Text>

            <Form.Item name="description" className="mg-0 second-column">
              <TextArea
                autoComplete={ONE_TIME_CODE}
                rows={4}
                maxLength={260}
                data-testid="description-input"
                onChange={(e) => {
                  setInputData({
                    event: e,
                    fieldName: 'description',
                    isEditPage: false,
                    isTextField: true,
                  });
                }}
              />
            </Form.Item>
          </div>
          <div className="container description-container">
            <Typography.Text className="label" strong>
              Примечание
            </Typography.Text>

            <Form.Item name="note" className="mg-0 second-column">
              <Input.TextArea
                autoComplete={ONE_TIME_CODE}
                rows={4}
                maxLength={260}
                onChange={(e) => {
                  setInputData({
                    event: e,
                    fieldName: 'Note',
                    isEditPage: false,
                    isTextField: true,
                  });
                }}
              />
            </Form.Item>
          </div>
          <Divider className="divider" />
          <div style={{ display: hasUserRightToChangeOwner ? 'flex' : 'none' }}>
            <Form.Item name="isAdmin" valuePropName="checked" className="mg-0">
              <Switch
                data-testid={
                  form.getFieldValue('isAdmin') ? 'switcher-on' : 'switcher-off'
                }
                onChange={(value) => {
                  setIsAdmin(!isAdmin);
                  setSwitchData({
                    value,
                    fieldName: 'isAdmin',
                  });
                }}
              />
            </Form.Item>
            <div style={{ marginLeft: '10px' }}>
              <Text strong className="label" data-testid="owner">
                Владелец <br />
              </Text>
              <Text type="secondary" data-testid="owner-description">
                Пользователь с наивысшими правами
              </Text>
            </div>
          </div>
          <div style={{ display: 'flex' }} data-testid="executor">
            <Form.Item
              name="alterIsEmployee"
              valuePropName="checked"
              className="mg-0"
            >
              <Switch
                data-testid={
                  form.getFieldValue('alterIsEmployee')
                    ? 'switcher-on'
                    : 'switcher-off'
                }
                onChange={(value) => {
                  setSwitchData({
                    value,
                    fieldName: 'alterIsEmployee',
                  });
                }}
              />
            </Form.Item>
            <div style={{ marginLeft: '10px' }}>
              <Text strong className="label" data-testid="executor">
                Сделать исполнителем <br />
              </Text>
              <Text type="secondary" data-testid="executor-description">
                При создании пользователя он также создастся как исполнитель
              </Text>
            </div>
          </div>
          <div
            className="branches-container"
            style={{ display: hasUserRightToChangeBranches ? 'block' : 'none' }}
          >
            <Typography.Title level={3} className="mg-0" data-testid="branches">
              {BRANCHES_LABEL}
            </Typography.Title>
            <Text type="secondary" data-testid="branches-hint">
              Укажите, в каких филиалах может работать пользователь
            </Text>
            <SetBranchesComponent
              open={open}
              onCancel={hideUserModal}
              selectedBranches={selectedBranches}
              setSelectedBranches={setSelectedBranches}
              allBranches={allBranches}
              showUserModal={showUserModal}
              tooltipLabel="Укажите, в каких филиалах может работать пользователь"
              setNewData={setNewData}
              newDataFieldValue="UserBranchIds"
            />
          </div>
          <Space
            direction="vertical"
            size="large"
            className="mg-0 second-column"
          >
            {error && errorType && (
              <CustomAlert
                errorType={errorType}
                serverError={error}
                setServerError={setError}
              />
            )}

            <Form.Item style={{ margin: '20px 0 0 0', width: '100%' }}>
              <Space className="space-button" style={{ width: '100%' }}>
                <Button
                  id="test-add-user-form-main-button"
                  className="button"
                  size="large"
                  type="primary"
                  onClick={addUser}
                  loading={isSaveButtonLoader}
                  disabled={isAddMoreButtonLoader}
                  data-testid="add-user-button"
                >
                  Добавить
                </Button>

                <Button
                  className="button"
                  size="large"
                  type="default"
                  onClick={addMoreUser}
                  loading={isAddMoreButtonLoader}
                  disabled={isSaveButtonLoader}
                  data-testid="save-and-add-another-user-button"
                >
                  Сохранить и добавить ещё
                </Button>
              </Space>
            </Form.Item>
          </Space>
        </Space>
      </Form>

      <FastCreationModal
        title="Создание должности"
        isModalOpen={isModalOpen}
        handleOk={handleOk}
        handleCancel={handleCancel}
        modalForm={modalForm}
        finishModal={finishModal}
        nameMaxLength={40}
        descriptionMaxLength={260}
      />
    </>
  );
};
